import React from 'react';
import { Card, Col, Row, Space, Tag, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import {
  verificationTokenAtom,
  careProviderConfiguration,
  updateSurveyAtom,
  interactionIdAtom,
  currentQuestionIndexAtom,
  currentFeedbackQuestionIndexAtom,
  updateFeedbackSurveyAtom,
  answeredFeedbackQuestionsAtom,
  feedbackSurveyCompleteAtom,
  enableResumingSurveysAtom,
  currentUserAtom,
} from '../../store';
import { checkScreenSize } from '../../utilities/breakpoints';
import { useGetQuery } from '../../api/hooks/useGetQuery';
import { Scripts, ScriptProps, ScriptWithContext } from '../../models/scripts.interface';
import Header from '../header';
import LoadingCard from '../loading_card';
import EmptyTray from '../empty_tray';
import ErrorAlert from '../error_alert';
import { COLORS } from 'themes/colors';
import { InteractionStatusEnum } from 'models/interactions.interface';

const { Paragraph, Title } = Typography;

const getContextParams = (item: ScriptWithContext): string => {
  const params = new URLSearchParams();
  if (item.staffMemberId) {
    params.set('staffMemberId', item.staffMemberId);
  }
  else {
    params.set('encounterId', item.encounterId ?? '');
    params.set('unitId', item.unitId ?? '');
  }
  return params.toString();
};

const Script = ({
  script,
  contextParams,
  index,
  interactionId,
  displayNewStatus,
  displayInProgressStatus,
}: ScriptProps) => {
  return (
    <Link
      to={`../survey/${script.id}?${contextParams}${
        interactionId ? `&interactionId=${interactionId}` : ''
      }`}
      relative="path"
      data-cy={`script_${index}`}
      data-script-name={script.name}
    >
      <Card
        hoverable
        styles={{
          body: {
            border: '1px solid #94B9D3',
            borderRadius: '8px',
            padding: '14px',
          },
        }}
        className="script"
      >
        <Row justify="space-between" align="middle">
          <Paragraph
            style={{
              color: '#2973BE',
              fontWeight: '700',
              fontSize: '20px',
              marginTop: '0px',
              marginBottom: '0px',
            }}
          >
            {script.name}
          </Paragraph>
          {displayNewStatus && (
            <Tag
              color="#FFB507"
              className="newStatus"
              style={{
                fontSize: '12px',
                fontWeight: '400',
                marginBottom: '0px',
              }}
            >
              New
            </Tag>
          )}
          {displayInProgressStatus && (
            <Tag color={COLORS.icon} className="inProgressStatus">
              In-Progress
            </Tag>
          )}
        </Row>
        <Paragraph
          style={{ fontWeight: '400', fontSize: '14px', marginTop: '-10px' }}
        >
          <ReactMarkdown remarkPlugins={[remarkBreaks]}>
            {script.description}
          </ReactMarkdown>
        </Paragraph>
      </Card>
    </Link>
  );
};

const LandingPage: React.FC<{
  homeScreenMessage?: string;
}> = ({ homeScreenMessage }) => {
  const [token] = useAtom(verificationTokenAtom);
  const [enableResumingSurveys] = useAtom(enableResumingSurveysAtom);
  const [careProviderStyles] = useAtom(careProviderConfiguration);
  const [, setFeedbackSurveyComplete] = useAtom(feedbackSurveyCompleteAtom);
  const [, setCurrentFeedbackQuestionIndex] = useAtom(
    currentFeedbackQuestionIndexAtom,
  );
  const [, setUpdatedSurveyResponse] = useAtom(updateSurveyAtom);
  const [, setUpdatedFeedbackSurveyResponse] = useAtom(
    updateFeedbackSurveyAtom,
  );
  const [, setAnsweredFeedbackQuestions] = useAtom(
    answeredFeedbackQuestionsAtom,
  );
  const [, setCurrentQuestionIndex] = useAtom(currentQuestionIndexAtom);
  const [interactionId, setInteractionId] = useAtom(interactionIdAtom);

  // Use decoded token from App.tsx instead of decoding locally
  const [currentUser] = useAtom(currentUserAtom);
  const firstName = currentUser?.userFirstName;

  const config = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const { data, exec, isError, isIdle, isPending, isSuccess }
    = useGetQuery<Scripts>('scripts', config);

  const screenSize = checkScreenSize();

  const clearOldQuestionnaireData = () => {
    setCurrentQuestionIndex(0);
    setCurrentFeedbackQuestionIndex(0);
    setUpdatedSurveyResponse(null);
    setAnsweredFeedbackQuestions([]);
    setUpdatedFeedbackSurveyResponse({ responses: [] });
    setFeedbackSurveyComplete(false);
    setInteractionId(0);
  };

  useEffect(() => {
    exec();
  }, []);

  // If user has completed a survey and goes back to the landing page, clear stale survey data from state.
  // This is intentionally mount-only: we only need to reset data that was left over from a previous survey.
  useEffect(() => {
    if (interactionId) {
      clearOldQuestionnaireData();
    }
  }, []);

  return (
    <div
      style={{
        backgroundImage: `linear-gradient(to right, ${
          isIdle || isPending
            ? COLORS.whiteL0
            : careProviderStyles.landingPageBackgroundColor
        }, ${COLORS.whiteL0})`,
        minHeight: '100vh',
      }}
      data-cy="landing_page_container"
    >
      <Header background={careProviderStyles?.colorBgHeader || 'transparent'} />
      <Row
        justify={screenSize === 'mobile' ? 'space-between' : 'center'}
        style={{
          height: '100%',
          padding: '50px 32px',
        }}
      >
        <Col
          xs={{ offset: 0, span: 24 }}
          md={{ offset: 0, span: 16 }}
          lg={{ offset: 0, span: 12 }}
        >
          <Row justify="space-between" align="middle">
            <Title level={2}>
              Welcome {firstName} 👋
            </Title>
          </Row>
          <Row justify="space-between" align="middle">
            <Col>
              <Paragraph style={{ margin: '40px 0 36px' }}>
                {homeScreenMessage}
              </Paragraph>
            </Col>
          </Row>

          <Row justify="space-between" align="middle">
            <Col style={{ width: '100%', backgroundColor: 'transparent' }}>
              <LoadingCard isLoading={isPending}>
                {isError && (
                  <ErrorAlert message="Oops! Something went wrong..." />
                )}
                {isSuccess
                  && (data?.length
                    ? (
                        <Space
                          direction="vertical"
                          size="small"
                          style={{ width: '100%' }}
                        >
                          {data.map((item, i) => {
                            const resumableInteractionId = item.lastInteractionDetails?.status !== InteractionStatusEnum.completed
                              && enableResumingSurveys ? item.lastInteractionDetails?.id : null;
                            const displayNewStatus = !item.lastInteractionDetails
                              && item.script.displayNewStatus;
                            const displayInProgressStatus = item.lastInteractionDetails?.status === InteractionStatusEnum.incomplete
                              && item.script.displayInProgressStatus;

                            return (
                              <Script
                                key={item.script.id}
                                script={item.script}
                                index={i}
                                interactionId={resumableInteractionId}
                                displayNewStatus={displayNewStatus}
                                displayInProgressStatus={displayInProgressStatus}
                                contextParams={getContextParams(item)}
                              />
                            );
                          })}
                        </Space>
                      )
                    : (
                        <EmptyTray />
                      ))}
              </LoadingCard>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};
export default LandingPage;
